import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { StatusBadge } from '../components/StatusBadge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Plus, MoreHorizontal, Pencil, Trash2, Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AssignmentsPage() {
  const [assignments, setAssignments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    project_id: '',
    user_id: '',
    allocated_hours_total: '',
    allocation_start_date: '',
    allocation_end_date: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [assignmentsRes, projectsRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/api/assignments`),
        axios.get(`${API_URL}/api/projects`),
        axios.get(`${API_URL}/api/users`),
      ]);
      setAssignments(assignmentsRes.data);
      setProjects(projectsRes.data.filter(p => p.is_active));
      setUsers(usersRes.data.filter(u => u.is_active));
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingAssignment(null);
    setFormData({
      project_id: '',
      user_id: '',
      allocated_hours_total: '',
      allocation_start_date: '',
      allocation_end_date: '',
    });
    setDialogOpen(true);
  };

  const openEditDialog = (assignment) => {
    setEditingAssignment(assignment);
    setFormData({
      project_id: assignment.project_id,
      user_id: assignment.user_id,
      allocated_hours_total: assignment.allocated_hours_total.toString(),
      allocation_start_date: assignment.allocation_start_date || '',
      allocation_end_date: assignment.allocation_end_date || '',
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const data = {
      ...formData,
      allocated_hours_total: parseFloat(formData.allocated_hours_total) || 0,
      allocation_start_date: formData.allocation_start_date || null,
      allocation_end_date: formData.allocation_end_date || null,
    };

    try {
      if (editingAssignment) {
        await axios.put(`${API_URL}/api/assignments/${editingAssignment.id}`, {
          allocated_hours_total: data.allocated_hours_total,
          allocation_start_date: data.allocation_start_date,
          allocation_end_date: data.allocation_end_date,
        });
        toast.success('Assignment updated successfully');
      } else {
        await axios.post(`${API_URL}/api/assignments`, data);
        toast.success('Assignment created successfully');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (assignment) => {
    if (!window.confirm('Are you sure you want to delete this assignment?')) return;

    try {
      await axios.delete(`${API_URL}/api/assignments/${assignment.id}`);
      toast.success('Assignment deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete assignment');
    }
  };

  const filteredAssignments = assignments.filter(
    (a) =>
      a.project_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      a.user_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      a.project_code?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Project Assignments</h1>
          <p className="text-muted-foreground">Assign users to projects and set allocations</p>
        </div>
        <Button onClick={openCreateDialog} data-testid="add-assignment-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Assignment
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search assignments..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="search-assignments-input"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Project</TableHead>
                <TableHead className="text-right">Allocated</TableHead>
                <TableHead className="text-right">Logged</TableHead>
                <TableHead className="text-right">Remaining</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAssignments.map((assignment) => (
                <TableRow key={assignment.id} data-testid={`assignment-row-${assignment.id}`}>
                  <TableCell>
                    <div>
                      <div className="font-medium">{assignment.user_name}</div>
                      <div className="text-xs text-muted-foreground">{assignment.user_email}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <div className="font-medium">{assignment.project_name}</div>
                      <div className="text-xs text-muted-foreground">{assignment.project_code}</div>
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-medium">{assignment.allocated_hours_total}h</TableCell>
                  <TableCell className="text-right text-primary font-medium">{assignment.logged_hours.toFixed(1)}h</TableCell>
                  <TableCell className={`text-right font-medium ${assignment.remaining_hours < 0 ? 'text-red-600' : ''}`}>
                    {assignment.remaining_hours.toFixed(1)}h
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={assignment.status} showIcon={false} />
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" data-testid={`assignment-menu-${assignment.id}`}>
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEditDialog(assignment)}>
                          <Pencil className="w-4 h-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDelete(assignment)} className="text-destructive">
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
              {filteredAssignments.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No assignments found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingAssignment ? 'Edit Assignment' : 'Create Assignment'}</DialogTitle>
            <DialogDescription>
              {editingAssignment ? 'Update allocation details' : 'Assign a user to a project'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {!editingAssignment && (
              <>
                <div className="space-y-2">
                  <Label>Project</Label>
                  <Select
                    value={formData.project_id}
                    onValueChange={(value) => {
                      // Auto-populate from selected project
                      const selectedProject = projects.find(p => p.id === value);
                      if (selectedProject) {
                        setFormData({
                          ...formData,
                          project_id: value,
                          allocated_hours_total: selectedProject.allocated_hours?.toString() || '',
                          allocation_start_date: selectedProject.start_date || '',
                          allocation_end_date: selectedProject.end_date || '',
                        });
                      } else {
                        setFormData({ ...formData, project_id: value });
                      }
                    }}
                    required
                  >
                    <SelectTrigger data-testid="assignment-project-select">
                      <SelectValue placeholder="Select project" />
                    </SelectTrigger>
                    <SelectContent>
                      {projects.map((project) => (
                        <SelectItem key={project.id} value={project.id}>
                          {project.name} ({project.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>User</Label>
                  <Select
                    value={formData.user_id}
                    onValueChange={(value) => setFormData({ ...formData, user_id: value })}
                    required
                  >
                    <SelectTrigger data-testid="assignment-user-select">
                      <SelectValue placeholder="Select user" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.name} ({(user.roles || [user.role]).join(', ')})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            <div className="space-y-2">
              <Label htmlFor="allocated_hours">Allocated Hours <span className="text-xs text-muted-foreground">(from project)</span></Label>
              <Input
                id="allocated_hours"
                type="number"
                step="0.5"
                min="0"
                value={formData.allocated_hours_total}
                onChange={(e) => setFormData({ ...formData, allocated_hours_total: e.target.value })}
                disabled={!editingAssignment}
                className={!editingAssignment ? "bg-muted" : ""}
                required
                data-testid="assignment-hours-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start_date">Start Date <span className="text-xs text-muted-foreground">(from project)</span></Label>
                <Input
                  id="start_date"
                  type="date"
                  value={formData.allocation_start_date}
                  onChange={(e) => setFormData({ ...formData, allocation_start_date: e.target.value })}
                  disabled={!editingAssignment}
                  className={!editingAssignment ? "bg-muted" : ""}
                  data-testid="assignment-start-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end_date">End Date <span className="text-xs text-muted-foreground">(from project)</span></Label>
                <Input
                  id="end_date"
                  type="date"
                  value={formData.allocation_end_date}
                  onChange={(e) => setFormData({ ...formData, allocation_end_date: e.target.value })}
                  disabled={!editingAssignment}
                  className={!editingAssignment ? "bg-muted" : ""}
                  data-testid="assignment-end-date-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="save-assignment-btn">
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  editingAssignment ? 'Update' : 'Create'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
